import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import * as maplibregl from 'maplibre-gl'
// MapLibre 6 builds its worker URL from a runtime variable, which bundlers
// can't see — so hand Vite the file explicitly; it fingerprints and ships it.
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?url'
import { parseMapHash, formatMapHash } from '../lib/mapHash.js'
import { sectionPaint, zoneLayers, selectedFilter } from '../lib/mapStyle.js'
import { basemapStyle, osmRasterStyle, localizedTextField, flatBuildings, zoneStacking, buildingsOver } from '../lib/basemaps.js'
import { circlePolygon } from '../lib/geo.js'

maplibregl.setWorkerUrl(workerUrl)

const PRAGUE = [14.42, 50.083]
const DEFAULT_ZOOM = 12.5
// URL hashes keep Leaflet-scale zoom (256 px tiles) so links saved before the
// move to MapLibre (512 px tiles) still open at the same scale.
const HASH_ZOOM_OFFSET = 1
// Generous box around Prague: room to look around, not to fling off to Vienna.
const MAX_BOUNDS = [
  [13.6, 49.7],
  [15.3, 50.45],
]
const ZONES = 'zones'
const ME = 'me-accuracy'
const EMPTY = { type: 'FeatureCollection', features: [] }
const ZERO_PAD = { top: 0, right: 0, bottom: 0, left: 0 }
// Finger-sized tolerance for picking a section (px around the tap).
const TAP_TOLERANCE = 12

/**
 * Bake a fill's opacity into its colour, as painted over the plain background,
 * so a copy drawn solid still looks like the original (a dark style may draw
 * its buildings at a quarter opacity).
 */
function flattenFill(map, id, opacity) {
  const color = map.getPaintProperty(id, 'fill-color')
  const bg = map.getStyle().layers.find((l) => l.type === 'background')?.paint?.['background-color']
  if (typeof color !== 'string' || typeof opacity !== 'number' || typeof bg !== 'string') return
  // Let the browser do the colour maths: paint one pixel and read it back.
  const ctx = Object.assign(document.createElement('canvas'), { width: 1, height: 1 }).getContext('2d')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, 1, 1)
  ctx.globalAlpha = opacity
  ctx.fillStyle = color
  ctx.fillRect(0, 0, 1, 1)
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
  map.setPaintProperty(id, 'fill-color', `rgb(${r}, ${g}, ${b})`)
}

/** Compact hover tooltip for one section: the quote in price mode, else status + Kč/h. */
function tipHtml(section, ctx) {
  const { colorMode, costs, statuses, t } = ctx
  const est = costs?.get(section.id)
  const st = statuses?.get(section.id)
  let val
  if (colorMode === 'price' && est) {
    val = !est.allowed ? t('heatBlockedShort') : est.fullyFree ? t('heatFree') : t('price', est.cost)
  } else if (st) {
    const price = st.activeRule?.price ?? section.price ?? section.rules?.[0]?.price
    val = st.free ? t('freeNow') : t('paidNow')
    if (price != null && !st.free) val += ` · ${t('pricePerHour', price)}`
  }
  return `<span class="zonetip__id">${section.id}</span><span class="zonetip__val">${val ?? ''}</span>`
}

/**
 * The map: MapLibre GL over OpenFreeMap vector tiles. All 5 508 sections are one
 * GeoJSON source; their colours are feature-state, so time/stay/filter changes
 * never re-tile geometry. Exposes zoomIn / zoomOut / resetNorth via ref.
 */
const MapView = forwardRef(function MapView(props, ref) {
  const {
    zones,
    selectedId,
    theme,
    basemap,
    occlusion,
    flyTo,
    searchPin,
    locateOn,
    follow,
    headingUp,
  } = props

  const el = useRef(null)
  const mapRef = useRef(null)
  const propsRef = useRef(props)
  propsRef.current = props
  const styleReady = useRef(false)
  const appliedStyleKey = useRef(null)
  const fellBack = useRef(false)
  const lastPaint = useRef([]) // per feature: serialized state last sent
  const selectedNum = useRef(null)
  const pinMarker = useRef(null)
  const meMarker = useRef(null)
  const lastFix = useRef(null)
  const lastHeading = useRef(null)
  const bearingRaf = useRef(0)
  const armFallback = useRef(() => {})
  const userRotated = useRef(false)

  // Numeric feature ids (index) — the most robust key for feature-state.
  const { data, idToNum } = useMemo(() => {
    if (!zones) return { data: null, idToNum: new Map() }
    const m = new Map()
    const features = zones.features.map((f, i) => {
      m.set(f.properties.id, i)
      return { type: 'Feature', id: i, geometry: f.geometry, properties: {} }
    })
    return { data: { type: 'FeatureCollection', features }, idToNum: m }
  }, [zones])
  const dataRef = useRef(data)
  dataRef.current = data

  // Camera padding: keep whatever we centre clear of the panel/sheet, the status
  // chips (top) and the control column (right).
  const padding = useMemo(
    () => ({ top: 76, right: 68, bottom: (occlusion?.bottom ?? 0) + 20, left: (occlusion?.left ?? 0) + 20 }),
    [occlusion?.bottom, occlusion?.left]
  )
  const paddingRef = useRef(padding)
  paddingRef.current = padding

  // --- paint every section from the current props (diffed) ----------------
  const applyPaint = () => {
    const map = mapRef.current
    if (!map || !styleReady.current || !map.getSource(ZONES)) return
    const { zones: z, statuses, costs, heatScale, colorMode, theme: th } = propsRef.current
    if (!z || !statuses) return
    const last = lastPaint.current
    for (let i = 0; i < z.features.length; i++) {
      const f = z.features[i]
      const status = statuses.get(f.properties.id)
      if (!status) continue
      const paint = sectionPaint(f, status, colorMode, costs?.get(f.properties.id), heatScale, th)
      const key = paint.fill + paint.stroke
      if (last[i] === key) continue
      map.setFeatureState({ source: ZONES, id: i }, paint)
      last[i] = key
    }
  }

  // Basemap place and street names in the app's language.
  const localizeLabels = () => {
    const map = mapRef.current
    if (!map) return
    const { lang } = propsRef.current
    for (const layer of map.getStyle()?.layers ?? []) {
      if (layer.type !== 'symbol') continue
      const field = localizedTextField(layer.layout?.['text-field'], lang)
      if (field) map.setLayoutProperty(layer.id, 'text-field', field)
    }
  }

  // --- (re)install our sources + layers after any style load --------------
  const install = () => {
    const map = mapRef.current
    if (!map) return
    styleReady.current = true
    localizeLabels()
    const flat = flatBuildings(map.getStyle().layers)
    for (const l of flat.remove) map.removeLayer(l.id)
    for (const l of flat.extend) map.setLayerZoomRange(l.id, l.minzoom ?? 0, 24)
    const d = dataRef.current
    if (d && !map.getSource(ZONES)) {
      map.addSource(ZONES, { type: 'geojson', data: d, tolerance: 0.3 })
      const { before, lift, buildings } = zoneStacking(map.getStyle().layers)
      for (const layer of zoneLayers(ZONES, propsRef.current.theme, selectedNum.current)) map.addLayer(layer, before)
      for (const b of buildings) {
        const over = buildingsOver(b)
        if (!over) continue
        map.addLayer(over, 'zones-selected-casing')
        // Solid, or the coarse polygons still show through the blocks.
        flattenFill(map, over.id, b.paint?.['fill-opacity'] ?? 1)
      }
      for (const id of lift) map.moveLayer(id, before)
      lastPaint.current = []
    }
    if (!map.getSource(ME)) {
      map.addSource(ME, { type: 'geojson', data: EMPTY })
      map.addLayer({ id: 'me-accuracy-fill', type: 'fill', source: ME, paint: { 'fill-color': '#1a73e8', 'fill-opacity': 0.12 } })
      map.addLayer({
        id: 'me-accuracy-line',
        type: 'line',
        source: ME,
        paint: { 'line-color': '#1a73e8', 'line-opacity': 0.45, 'line-width': 1 },
      })
      const fix = lastFix.current
      if (fix?.accuracy) map.getSource(ME).setData(circlePolygon(fix.lng, fix.lat, fix.accuracy))
    }
    applyPaint()
  }

  // --- create the map once -------------------------------------------------
  // Deferred to a task and cancelled by cleanup, so React StrictMode's
  // mount → unmount → mount builds ONE map instead of building, destroying and
  // rebuilding it. That matters beyond the wasted work: in MapLibre 6, a map
  // created after another was removed never gets its tiles from the workers.
  // (A timeout, not requestAnimationFrame: rAF is paused in background tabs
  // and some webviews, which would leave the map uncreated.)
  useEffect(() => {
    let disposeMap = null
    const timer = setTimeout(() => {
      disposeMap = createMap()
    }, 0)
    return () => {
      clearTimeout(timer)
      disposeMap?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function createMap() {
    const saved = parseMapHash(window.location.hash)
    const { basemap: bm, theme: th, t } = propsRef.current
    let map
    try {
      map = new maplibregl.Map({
        container: el.current,
        style: basemapStyle(bm, th),
        center: saved ? [saved.lng, saved.lat] : PRAGUE,
        zoom: saved ? saved.zoom - HASH_ZOOM_OFFSET : DEFAULT_ZOOM,
        maxBounds: MAX_BOUNDS,
        minZoom: 9,
        maxZoom: 19,
        attributionControl: false, // credits sit in the panel footer, under the data source
        pitchWithRotate: false,
        touchPitch: false,
        maxPitch: 0,
        fadeDuration: 120,
        locale: { 'Map.Title': t('mapLabel') },
      })
    } catch {
      propsRef.current.onError?.('webgl')
      return
    }
    appliedStyleKey.current = `${bm}|${th}`
    mapRef.current = map
    if (import.meta.env.DEV) window.__zpspMap = map // devtools handle; stripped from builds

    map.on('style.load', install)

    // If a vector basemap can't load (provider outage — what CARTO did to the
    // old raster tiles), fall back to OSM's raster once instead of a blank map.
    const styleTimer = { id: 0 }
    armFallback.current = () => {
      clearTimeout(styleTimer.id)
      styleTimer.id = setTimeout(() => {
        if (!styleReady.current) fallBack()
      }, 9000)
    }
    const fallBack = () => {
      if (fellBack.current || !mapRef.current) return
      fellBack.current = true
      styleReady.current = false
      map.setStyle(osmRasterStyle(propsRef.current.theme), { diff: false })
      propsRef.current.onBasemapFallback?.()
    }
    map.on('error', (e) => {
      if (!styleReady.current && /style|sprite|glyph|tiles\.openfreemap/i.test(String(e?.error?.url || e?.error?.message || ''))) {
        fallBack()
      }
    })
    map.on('style.load', () => clearTimeout(styleTimer.id))
    armFallback.current()

    // Tap: the section under the finger, else the nearest within a fingertip.
    map.on('click', (e) => {
      const p = propsRef.current
      if (!map.getLayer('zones-fill')) return p.onMapTap?.()
      let hit = map.queryRenderedFeatures(e.point, { layers: ['zones-fill'] })[0]
      if (!hit) {
        const r = TAP_TOLERANCE
        const seen = new Set()
        const near = map
          .queryRenderedFeatures(
            [
              [e.point.x - r, e.point.y - r],
              [e.point.x + r, e.point.y + r],
            ],
            { layers: ['zones-fill', 'zones-line'] }
          )
          .filter((f) => !seen.has(f.id) && seen.add(f.id))
        let best = Infinity
        for (const f of near) {
          const sid = p.zones?.features[f.id]?.properties.id
          const c = p.centroids?.get(sid)
          if (!c) continue
          const q = map.project([c.lng, c.lat])
          const d = Math.hypot(q.x - e.point.x, q.y - e.point.y)
          if (d < best) {
            best = d
            hit = f
          }
        }
      }
      if (hit) p.onSelect?.(p.zones.features[hit.id].properties.id)
      else p.onMapTap?.()
    })

    // Hover tooltip (mouse/trackpad only).
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'zonetip',
      offset: 14,
      maxWidth: 'none',
    })
    let hoverRaf = 0
    map.on('mousemove', (e) => {
      cancelAnimationFrame(hoverRaf)
      hoverRaf = requestAnimationFrame(() => {
        const p = propsRef.current
        if (!p.showHover || !map.getLayer('zones-fill')) return
        const f = map.queryRenderedFeatures(e.point, { layers: ['zones-fill'] })[0]
        if (!f) {
          popup.remove()
          map.getCanvas().style.cursor = ''
          return
        }
        map.getCanvas().style.cursor = 'pointer'
        popup.setLngLat(e.lngLat).setHTML(tipHtml(p.zones.features[f.id].properties, p)).addTo(map)
      })
    })
    map.getCanvas().addEventListener('mouseleave', () => {
      popup.remove()
      map.getCanvas().style.cursor = ''
    })

    // A hand pan ends "follow my location"; a hand rotate ends "heading up".
    map.on('dragstart', () => {
      if (propsRef.current.follow) propsRef.current.onFollowBreak?.()
    })
    map.on('rotatestart', (e) => {
      if (e.originalEvent && propsRef.current.headingUp) {
        userRotated.current = true // keep the user's own angle; don't snap north
        propsRef.current.onHeadingBreak?.()
      }
    })
    map.on('rotate', () => {
      cancelAnimationFrame(bearingRaf.current)
      bearingRaf.current = requestAnimationFrame(() => propsRef.current.onBearing?.(map.getBearing()))
    })

    // URL hash = the container centre (not the padded centre), so a link opens
    // the same view whatever panel the receiver has open.
    map.on('moveend', () => {
      const { clientWidth: w, clientHeight: h } = map.getContainer()
      const c = map.unproject([w / 2, h / 2])
      const next = formatMapHash({ zoom: map.getZoom() + HASH_ZOOM_OFFSET, lat: c.lat, lng: c.lng })
      if (next !== window.location.hash) window.history.replaceState(null, '', next)
    })
    const onHashChange = () => {
      const hh = parseMapHash(window.location.hash)
      if (hh) map.jumpTo({ center: [hh.lng, hh.lat], zoom: hh.zoom - HASH_ZOOM_OFFSET, padding: ZERO_PAD })
    }
    window.addEventListener('hashchange', onHashChange)

    return () => {
      clearTimeout(styleTimer.id)
      cancelAnimationFrame(hoverRaf)
      cancelAnimationFrame(bearingRaf.current)
      window.removeEventListener('hashchange', onHashChange)
      popup.remove()
      map.remove()
      mapRef.current = null
      styleReady.current = false
      meMarker.current = null
      pinMarker.current = null
    }
  }

  useImperativeHandle(ref, () => ({
    zoomIn: () => mapRef.current?.zoomIn({ duration: 250 }),
    zoomOut: () => mapRef.current?.zoomOut({ duration: 250 }),
    resetNorth: () => mapRef.current?.resetNorth({ duration: 400 }),
  }))

  // --- basemap / theme switch ---------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    const key = `${basemap}|${theme}`
    if (!map || appliedStyleKey.current === key) return
    appliedStyleKey.current = key
    fellBack.current = false
    styleReady.current = false
    map.setStyle(basemapStyle(basemap, theme), { diff: false })
    armFallback.current()
  }, [basemap, theme])

  useEffect(() => {
    if (styleReady.current) localizeLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.lang])

  // --- zones data arrives / changes ----------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReady.current || !data) return
    const src = map.getSource(ZONES)
    if (src) {
      src.setData(data)
      lastPaint.current = []
      applyPaint()
    } else install()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // --- re-colour on any input that changes a section's paint ---------------
  useEffect(() => {
    applyPaint()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.statuses, props.costs, props.heatScale, props.colorMode, theme])

  // --- selected outline ----------------------------------------------------
  useEffect(() => {
    selectedNum.current = selectedId != null ? (idToNum.get(selectedId) ?? null) : null
    const map = mapRef.current
    if (!map || !styleReady.current || !map.getLayer('zones-selected')) return
    const f = selectedFilter(selectedNum.current)
    map.setFilter('zones-selected', f)
    map.setFilter('zones-selected-casing', f)
  }, [selectedId, idToNum])

  // --- keep a tapped section clear of the sheet/panel ----------------------
  // Runs again when the panel settles at a new height, so a section revealed at
  // "peek" is re-checked once the card has grown to half the screen.
  useEffect(() => {
    const map = mapRef.current
    const c = selectedId && propsRef.current.centroids?.get(selectedId)
    if (!map || !c) return
    const { clientWidth: w, clientHeight: h } = map.getContainer()
    const q = map.project([c.lng, c.lat])
    const m = 24
    const inside =
      q.x >= padding.left + m && q.x <= w - padding.right - m && q.y >= padding.top + m && q.y <= h - padding.bottom - m
    if (!inside) map.easeTo({ center: [c.lng, c.lat], padding, duration: 450 })
  }, [selectedId, padding])

  // --- fly to a search result ----------------------------------------------
  // A beat late on purpose: picking a result also collapses the sheet, and the
  // panel reports its new height after this effect. Flying with the old
  // (full-sheet) padding would park the destination high above the centre.
  useEffect(() => {
    if (!flyTo) return
    const timer = setTimeout(() => {
      const map = mapRef.current
      if (!map) return
      map.flyTo({
        center: [flyTo.lng, flyTo.lat],
        zoom: flyTo.zoom ?? Math.max(map.getZoom(), 15),
        padding: paddingRef.current,
        duration: 900,
      })
    }, 60)
    return () => clearTimeout(timer)
  }, [flyTo])

  // --- search destination pin ----------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    pinMarker.current?.remove()
    pinMarker.current = null
    if (!map || !searchPin) return
    const pin = document.createElement('div')
    pin.className = 'searchpin'
    pinMarker.current = new maplibregl.Marker({ element: pin, anchor: 'bottom' })
      .setLngLat([searchPin.lng, searchPin.lat])
      .addTo(map)
  }, [searchPin])

  // --- live location: a watch you switch on and off ------------------------
  useEffect(() => {
    const map = mapRef.current
    const clear = () => {
      meMarker.current?.remove()
      meMarker.current = null
      lastFix.current = null
      lastHeading.current = null
      map?.getSource(ME)?.setData(EMPTY)
    }
    if (!map) return
    if (!locateOn) {
      clear()
      return
    }
    if (!('geolocation' in navigator)) {
      propsRef.current.onLocateError?.('unsupported')
      return
    }
    let first = true
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy, heading, speed } = pos.coords
        // Heading is only meaningful while moving; a parked phone reports NaN or
        // a stale value, and a cone pointing the wrong way is worse than none.
        const moving = Number.isFinite(heading) && (speed == null || speed > 0.7)
        if (moving) lastHeading.current = heading
        lastFix.current = { lat, lng, accuracy }
        map.getSource(ME)?.setData(accuracy ? circlePolygon(lng, lat, accuracy) : EMPTY)

        if (!meMarker.current) {
          const dot = document.createElement('div')
          dot.className = 'me'
          dot.innerHTML = '<div class="me__cone"></div><div class="me__dot"></div>'
          meMarker.current = new maplibregl.Marker({ element: dot, rotationAlignment: 'map', pitchAlignment: 'map' })
            .setLngLat([lng, lat])
            .addTo(map)
        } else meMarker.current.setLngLat([lng, lat])
        meMarker.current.getElement().classList.toggle('has-heading', moving)
        if (moving) {
          meMarker.current.setRotation(heading)
          propsRef.current.onHeading?.(true)
        }

        const p = propsRef.current
        if (first) {
          first = false
          map.easeTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 15.5), padding: paddingRef.current, duration: 800 })
          p.onLocated?.()
        } else if (p.follow) {
          map.easeTo({
            center: [lng, lat],
            bearing: p.headingUp && lastHeading.current != null ? lastHeading.current : map.getBearing(),
            padding: paddingRef.current,
            duration: 700,
          })
        }
      },
      (err) => {
        clear()
        propsRef.current.onLocateError?.(err.code === 1 ? 'denied' : err.code === 3 ? 'timeout' : 'unavailable')
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 }
    )
    return () => navigator.geolocation.clearWatch(watchId)
  }, [locateOn])

  // Resuming follow (tap "recenter" after panning away) snaps back right away.
  useEffect(() => {
    const map = mapRef.current
    const fix = lastFix.current
    if (!map || !follow || !fix) return
    map.easeTo({ center: [fix.lng, fix.lat], zoom: Math.max(map.getZoom(), 15), padding: paddingRef.current, duration: 450 })
  }, [follow])

  // Heading-up turns the map with your direction of travel; leaving it re-norths
  // — unless you left it by rotating the map yourself, then your angle stays.
  const prevHeadingUp = useRef(false)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (headingUp && lastHeading.current != null) {
      map.easeTo({ bearing: lastHeading.current, padding: paddingRef.current, duration: 500 })
    } else if (!headingUp && prevHeadingUp.current && !userRotated.current) {
      map.easeTo({ bearing: 0, duration: 500 })
    }
    userRotated.current = false
    prevHeadingUp.current = headingUp
  }, [headingUp])

  return <div ref={el} className="map" />
})

export default MapView
